'use client';

import { useCallback, useMemo } from 'react';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import ContactDetailsDrawer from '@/app/components/signals/ContactDetailsDrawer';
import { ContactDetailsDrawerContext } from '@/app/context/contact-details-drawer';
import { useClientCreditAccess } from '@/app/components/credits/CreditAccessGate';

export default function ContactDetailsDrawerProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { clientId } = useParams<{ clientId?: string }>();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const creditAccess = useClientCreditAccess(clientId);
  const contactId =
    searchParams.get('drawer') === 'contact' ? searchParams.get('drawer_id') || '' : '';
  const drawerTab = searchParams.get('drawer_tab');
  const initialTab = drawerTab === 'signals' || drawerTab === 'company' ? drawerTab : 'details';
  const routeSignalTab = pathname.endsWith('/signals/market')
    ? 'market'
    : pathname.endsWith('/signals/linkedin')
      ? 'linkedin'
      : 'website';
  const signalTabParam = searchParams.get('drawer_signal_tab');
  const initialSignalTab =
    signalTabParam === 'market' || signalTabParam === 'website' || signalTabParam === 'linkedin'
      ? signalTabParam
      : routeSignalTab;

  const updateDrawerParams = useCallback(
    (
      nextContactId?: string,
      tab: 'details' | 'signals' | 'company' = 'details',
      signalTab?: 'website' | 'market' | 'linkedin'
    ) => {
      const params = new URLSearchParams(searchParams.toString());

      if (nextContactId) {
        params.set('drawer', 'contact');
        params.set('drawer_id', nextContactId);
        if (tab === 'details') params.delete('drawer_tab');
        else params.set('drawer_tab', tab);
        if (tab === 'signals') params.set('drawer_signal_tab', signalTab ?? routeSignalTab);
        else params.delete('drawer_signal_tab');
      } else {
        params.delete('drawer');
        params.delete('drawer_id');
        params.delete('drawer_tab');
        params.delete('drawer_signal_tab');
      }

      const query = params.toString();
      const href = query ? `${pathname}?${query}` : pathname;
      if (nextContactId) {
        router.push(href, { scroll: false });
      } else {
        router.replace(href, { scroll: false });
      }
    },
    [pathname, routeSignalTab, router, searchParams]
  );

  const openContactDetails = useCallback(
    (
      nextContactId: string,
      tab: 'details' | 'signals' | 'company' = 'details',
      signalTab?: 'website' | 'market' | 'linkedin'
    ) => {
      if (creditAccess.isExhausted) return;
      updateDrawerParams(nextContactId, tab, signalTab);
    },
    [creditAccess.isExhausted, updateDrawerParams]
  );
  const closeContactDetails = useCallback(() => {
    updateDrawerParams();
  }, [updateDrawerParams]);
  const changeSignalTab = useCallback(
    (signalTab: 'website' | 'market' | 'linkedin') => {
      if (!contactId) return;
      updateDrawerParams(contactId, 'signals', signalTab);
    },
    [contactId, updateDrawerParams]
  );
  const contextValue = useMemo(
    () => ({ openContactDetails, closeContactDetails }),
    [closeContactDetails, openContactDetails]
  );

  return (
    <ContactDetailsDrawerContext.Provider value={contextValue}>
      {children}
      <ContactDetailsDrawer
        contactId={contactId}
        initialTab={initialTab}
        initialSignalTab={initialSignalTab}
        onSignalTabChange={changeSignalTab}
        isOpen={Boolean(contactId) && !creditAccess.isLoading && !creditAccess.isExhausted}
        onOpenChange={open => {
          if (!open) closeContactDetails();
        }}
      />
    </ContactDetailsDrawerContext.Provider>
  );
}
